import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import GlassCard from "../../components/dashboard/GlassCard";
import Header from "../../components/Header";
import saasApi from "../../utils/saasApi";

const ACTIONS = [
  "",
  "auth.login",
  "auth.logout",
  "user.",
  "camera.",
  "branch.",
  "ai_config.",
  "ai_instance.",
  "ai_schedule.",
  "contact.",
  "alert.",
  "clip.",
];

const ENTITIES = [
  "",
  "user",
  "camera",
  "branch",
  "detections_config",
  "notification_contact",
  "alert",
  "clip",
  "ai_instance",
  "session",
];

const formatDetails = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value);
  } catch (_) {
    return String(value);
  }
};

const AuditLog = () => {
  const [rows, setRows] = useState([]);
  const [draft, setDraft] = useState({
    q: "",
    action: "",
    entity_type: "",
    ip: "",
    date_from: "",
    date_to: "",
  });
  const [filters, setFilters] = useState(draft);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await saasApi.getAuditLogs(filters);
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setRows([]);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const summary = useMemo(() => {
    const failedLogins = rows.filter((row) => row.action === "auth.login.failed").length;
    const writes = rows.filter((row) => !String(row.action || "").includes(".list")).length;
    const users = new Set(rows.map((row) => row.user_id).filter(Boolean)).size;
    return { total: rows.length, writes, failedLogins, users };
  }, [rows]);

  const columns = useMemo(() => [
    {
      field: "created_at",
      headerName: "Fecha",
      minWidth: 180,
      valueFormatter: ({ value }) => (value ? new Date(value).toLocaleString("es-AR") : ""),
    },
    {
      field: "action",
      headerName: "Accion",
      minWidth: 190,
      renderCell: ({ value }) => <Chip label={value || "-"} size="small" variant="outlined" />,
    },
    {
      field: "entity_type",
      headerName: "Entidad",
      minWidth: 140,
      valueGetter: ({ row }) => [row.entity_type, row.entity_id].filter(Boolean).join(" #"),
    },
    {
      field: "user",
      headerName: "Usuario",
      minWidth: 220,
      valueGetter: ({ row }) => row.email || row.username || (row.user_id ? `ID ${row.user_id}` : "Sistema"),
    },
    { field: "organization_name", headerName: "Organizacion", minWidth: 180 },
    { field: "branch_name", headerName: "Sucursal", minWidth: 160 },
    { field: "ip_address", headerName: "IP", minWidth: 140 },
    {
      field: "user_agent",
      headerName: "User agent",
      minWidth: 260,
      renderCell: ({ value }) => (
        <Tooltip title={value || ""}>
          <Typography variant="body2" noWrap>{value || "-"}</Typography>
        </Tooltip>
      ),
    },
    {
      field: "details",
      headerName: "Payload",
      flex: 1,
      minWidth: 260,
      renderCell: ({ value }) => {
        const text = formatDetails(value);
        return (
          <Tooltip title={text}>
            <Typography variant="body2" noWrap>{text || "-"}</Typography>
          </Tooltip>
        );
      },
    },
  ], []);

  const updateFilter = (key, value) => {
    setDraft((current) => ({ ...current, [key]: value }));
  };

  const applyFilters = (event) => {
    event.preventDefault();
    if ((!draft.date_from || !draft.date_to || draft.date_from <= draft.date_to) && JSON.stringify(draft) !== JSON.stringify(filters)) setFilters(draft);
  };
  const metric = (value) => loading || error ? "—" : value;
  const invalidPeriod = Boolean(draft.date_from && draft.date_to && draft.date_from > draft.date_to);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box className="scene-toolbar" display="flex" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap" mb={2}>
        <Header title="Auditoria" subtitle="Acciones sensibles, usuarios, IP, user agent y payload relevante" />
        <Button variant="contained" startIcon={<RefreshIcon />} onClick={fetchRows} disabled={loading}>Actualizar</Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={3}>
          <GlassCard hover={false}><Typography color="text.secondary">Eventos cargados</Typography><Typography variant="h4">{metric(summary.total)}</Typography></GlassCard>
        </Grid>
        <Grid item xs={12} md={3}>
          <GlassCard hover={false}><Typography color="text.secondary">Cambios</Typography><Typography variant="h4">{metric(summary.writes)}</Typography></GlassCard>
        </Grid>
        <Grid item xs={12} md={3}>
          <GlassCard hover={false}><Typography color="text.secondary">Logins fallidos en resultados</Typography><Typography variant="h4">{metric(summary.failedLogins)}</Typography></GlassCard>
        </Grid>
        <Grid item xs={12} md={3}>
          <GlassCard hover={false}><Typography color="text.secondary">Usuarios</Typography><Typography variant="h4">{metric(summary.users)}</Typography></GlassCard>
        </Grid>
      </Grid>

      <GlassCard hover={false} sx={{ mb: 2 }}>
        <Box component="form" onSubmit={applyFilters}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField fullWidth size="small" label="Buscar" value={draft.q} onChange={(e) => updateFilter("q", e.target.value)} InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1 }} /> }} />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField select fullWidth size="small" label="Accion" value={draft.action} onChange={(e) => updateFilter("action", e.target.value)}>
              {ACTIONS.map((action) => <MenuItem key={action || "all"} value={action}>{action || "Todas"}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField select fullWidth size="small" label="Entidad" value={draft.entity_type} onChange={(e) => updateFilter("entity_type", e.target.value)}>
              {ENTITIES.map((entity) => <MenuItem key={entity || "all"} value={entity}>{entity || "Todas"}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField fullWidth size="small" label="IP" value={draft.ip} onChange={(e) => updateFilter("ip", e.target.value)} />
          </Grid>
          <Grid item xs={12} md={1.5}>
            <TextField fullWidth size="small" type="date" label="Desde" InputLabelProps={{ shrink: true }} value={draft.date_from} onChange={(e) => updateFilter("date_from", e.target.value)} />
          </Grid>
          <Grid item xs={12} md={1.5}>
            <TextField fullWidth size="small" type="date" label="Hasta" InputLabelProps={{ shrink: true }} value={draft.date_to} onChange={(e) => updateFilter("date_to", e.target.value)} />
          </Grid>
        </Grid>
        {invalidPeriod && <Alert severity="warning" sx={{ mt: 2 }}>El período de auditoría es inválido.</Alert>}
        <Button type="submit" variant="contained" sx={{ mt: 2 }} disabled={loading || invalidPeriod || JSON.stringify(draft) === JSON.stringify(filters)}>Aplicar filtros</Button>
        </Box>
      </GlassCard>

      {!error && <GlassCard hover={false} sx={{ height: 650, p: 0, overflow: "hidden" }}>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row.id}
          disableRowSelectionOnClick
          density="compact"
          pageSizeOptions={[25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          slots={{
            noRowsOverlay: () => (
              <Stack alignItems="center" justifyContent="center" height="100%">
                <Typography color="text.secondary">No hay eventos para los filtros seleccionados.</Typography>
              </Stack>
            ),
          }}
        />
      </GlassCard>}
    </Box>
  );
};

export default AuditLog;
